/**
 * Comparison of two iperf suites: each side is one or more runs (a whole
 * test = one run per CAP tree, or a single tree, even from different
 * tests). Tests are matched by type and endpoints (hop a→b, end-to-end
 * leaf→CAP, device→CAP) and each matched pair gets a verdict: better,
 * worse, mixed (throughput and loss disagree) or same (within the
 * thresholds). The older side is the baseline; "better" means the newer
 * side improved.
 */

export const IPERF_COMPARE_DIFF_PCT = 10;
export const IPERF_COMPARE_LOSS_PP = 1;

const findRawPerf = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.perf && typeof raw.perf === 'object') return raw.perf;
  if (raw.status?.perf && typeof raw.status.perf === 'object') return raw.status.perf;
  return null;
};

export function iperfTestThroughput(t) {
  if (typeof t?.throughput_mbps === 'number' && t.throughput_mbps >= 0) return t.throughput_mbps;
  const perf = findRawPerf(t?.raw_result);
  if (typeof perf?.bw !== 'number') return null;
  const unit = String(perf.bw_unit || '').toLowerCase();
  if (unit.includes('gbits')) return perf.bw * 1000;
  if (unit.includes('kbits')) return perf.bw / 1000;
  return perf.bw;
}

export function iperfTestLoss(t) {
  if (typeof t?.packet_loss_pct === 'number' && t.packet_loss_pct >= 0) return t.packet_loss_pct;
  const dl = findRawPerf(t?.raw_result)?.data_loss;
  if (dl == null) return null;
  const parsed = parseFloat(String(dl).replace('%', ''));
  return Number.isNaN(parsed) ? null : parsed;
}

export function iperfCompareKey(t) {
  if (!t?.type || !t.from_serial || !t.to_serial) return null;
  return `${t.type}:${String(t.from_serial).toLowerCase()}:${String(t.to_serial).toLowerCase()}`;
}

export const iperfCompareTypeLabel = (type) => {
  if (type === 'end_to_end') return 'E2E';
  if (type === 'all_to_cap') return 'To CAP';
  return 'Hop';
};

const capOfRun = (run) => (Array.isArray(run?.cap_serials) && run.cap_serials.length === 1 ? run.cap_serials[0] : run?.cap_serial) || '';

/** Completed, comparable tests of a suite, keyed; later runs win duplicates. */
function suiteTests(runs) {
  const out = new Map();
  (runs || []).forEach((run) => {
    (run?.tests || []).forEach((t) => {
      if (t?.status !== 'completed') return;
      if (!['hop', 'end_to_end', 'all_to_cap'].includes(t.type)) return;
      const key = iperfCompareKey(t);
      if (key) out.set(key, { test: t, cap: String(capOfRun(run)).toLowerCase() });
    });
  });
  return out;
}

const pct = (before, after) => (typeof before === 'number' && typeof after === 'number' && before > 0
  ? ((after - before) / before) * 100
  : null);

/**
 * Compare two suites. Returns rows for every matched test (changed ones
 * first), counts, and a per-tree breakdown.
 *
 * @param {Array} olderRuns baseline runs
 * @param {Array} newerRuns runs to judge
 */
export function compareIperfSuites(olderRuns, newerRuns, { diffPct = IPERF_COMPARE_DIFF_PCT, lossPP = IPERF_COMPARE_LOSS_PP } = {}) {
  const before = suiteTests(olderRuns);
  const after = suiteTests(newerRuns);
  const rows = [];
  const summary = {
    matched: 0, better: 0, worse: 0, mixed: 0, same: 0,
    throughputImproved: 0, throughputDegraded: 0, lossImproved: 0, lossDegraded: 0,
    unmatchedOlder: 0, unmatchedNewer: 0,
  };
  const trees = new Map();
  before.forEach(({ test: b, cap }, key) => {
    const entry = after.get(key);
    if (!entry) {
      summary.unmatchedOlder += 1;
      return;
    }
    const a = entry.test;
    summary.matched += 1;
    const beforeMbps = iperfTestThroughput(b);
    const afterMbps = iperfTestThroughput(a);
    const throughputPct = pct(beforeMbps, afterMbps);
    const throughputDelta = typeof beforeMbps === 'number' && typeof afterMbps === 'number' ? afterMbps - beforeMbps : null;
    const zeroBaseline = beforeMbps === 0 && typeof afterMbps === 'number' && afterMbps > 0;
    const throughputSignificant = zeroBaseline || (throughputPct != null && Math.abs(throughputPct) >= diffPct);
    const beforeLoss = iperfTestLoss(b);
    const afterLoss = iperfTestLoss(a);
    const lossDeltaPP = typeof beforeLoss === 'number' && typeof afterLoss === 'number' ? afterLoss - beforeLoss : null;
    const lossPct = pct(beforeLoss, afterLoss);
    const lossSignificant = typeof lossDeltaPP === 'number'
      && (Math.abs(lossDeltaPP) >= lossPP || (lossPct != null && Math.abs(lossPct) >= diffPct));
    if (throughputSignificant) {
      if (throughputDelta > 0) summary.throughputImproved += 1;
      if (throughputDelta < 0) summary.throughputDegraded += 1;
    }
    if (lossSignificant) {
      if (lossDeltaPP < 0) summary.lossImproved += 1;
      if (lossDeltaPP > 0) summary.lossDegraded += 1;
    }
    const good = (throughputSignificant && throughputDelta > 0) || (lossSignificant && lossDeltaPP < 0);
    const bad = (throughputSignificant && throughputDelta < 0) || (lossSignificant && lossDeltaPP > 0);
    let verdict = 'same';
    if (good && bad) verdict = 'mixed';
    else if (good) verdict = 'better';
    else if (bad) verdict = 'worse';
    summary[verdict] += 1;
    const score = Math.max(zeroBaseline ? 100 : Math.abs(throughputPct || 0), Math.abs(lossPct || 0), Math.abs(lossDeltaPP || 0) * 10);
    rows.push({
      key, type: b.type, from: b.from_serial, to: b.to_serial, cap: entry.cap || cap,
      beforeMbps, afterMbps, throughputDelta, throughputPct, throughputSignificant,
      beforeLoss, afterLoss, lossDeltaPP, lossPct, lossSignificant,
      verdict, score,
    });
    const tree = entry.cap || cap || '';
    const tr = trees.get(tree) || { cap: tree, matched: 0, better: 0, worse: 0, mixed: 0, same: 0 };
    tr.matched += 1;
    tr[verdict] += 1;
    trees.set(tree, tr);
  });
  after.forEach((_v, key) => {
    if (!before.has(key)) summary.unmatchedNewer += 1;
  });
  const order = { worse: 0, mixed: 1, better: 2, same: 3 };
  rows.sort((x, y) => (order[x.verdict] - order[y.verdict]) || (y.score - x.score) || x.key.localeCompare(y.key));
  const overall = summary.matched === 0 ? 'none'
    : summary.worse > summary.better ? 'worse'
      : summary.better > summary.worse ? 'better'
        : summary.better === 0 && summary.worse === 0 && summary.mixed === 0 ? 'same' : 'mixed';
  return { rows, summary, overall, trees: [...trees.values()].sort((x, y) => x.cap.localeCompare(y.cap)) };
}

/** Rows for a filter: all, changed (not same), better, worse. */
export function filterCompareRows(rows, filter) {
  switch (filter) {
    case 'changed': return rows.filter((r) => r.verdict !== 'same');
    case 'better': return rows.filter((r) => r.verdict === 'better');
    case 'worse': return rows.filter((r) => r.verdict === 'worse' || r.verdict === 'mixed');
    default: return rows;
  }
}

/** One line: "Newer is better: 12 better, 3 worse, 1 mixed, 40 unchanged of 56 matched tests." */
export function describeCompareSummary(result) {
  const s = result?.summary;
  if (!s || s.matched === 0) return 'No matching tests between the two: nothing to compare.';
  const head = {
    better: 'The newer run is better overall', worse: 'The newer run is worse overall',
    mixed: 'Mixed result', same: 'No meaningful change',
  }[result.overall];
  return `${head}: ${s.better} better, ${s.worse} worse, ${s.mixed} mixed, ${s.same} unchanged of ${s.matched} matched test${s.matched === 1 ? '' : 's'}.`;
}
